import { CommandDeckAccessHoldPanel } from '../components/CommandDeckAccessHoldPanel'
import type { CommandDeckPreviewBundle, CommandDeckSurfaceMode } from '../contracts/types'
import type { CommandDeckContinueResolution } from '../lib/resolveCommandDeckContinue'
import { deriveCommandDeckDeckPulse } from '../lib/commandDeckDeckPulse'
import { deriveCommandDeckWorkContext } from '../lib/commandDeckWorkContext'
import { CommandDeckAmbientBand } from './CommandDeckAmbientBand'
import { CommandDeckSpatialMap } from './CommandDeckSpatialMap'
import { CommandDeckStatusPill } from './CommandDeckStatusPill'
import { CommandDeckWorkContext } from './CommandDeckWorkContext'
import { synkPulseStatusPill } from './commandDeckStatusPillPresentational'
import { deriveSignalStrength } from './missionControlModel'

interface Props {
  bundle: CommandDeckPreviewBundle
  unlocked: boolean
  mode: CommandDeckSurfaceMode
  continueResolution: CommandDeckContinueResolution
  onPrimaryCta?: () => void
}

function MissionPulseStrip({
  bundle,
  mode,
}: {
  bundle: CommandDeckPreviewBundle
  mode: CommandDeckSurfaceMode
}) {
  const signal = deriveSignalStrength(bundle)
  const momentum = signal >= 75 ? 'High' : signal >= 45 ? 'Steady' : 'Building'
  const statusPill = synkPulseStatusPill(mode)

  return (
    <dl className="cd-mc-cockpit__synk-pulse" aria-label="Synk pulse">
      <div>
        <dt>Status</dt>
        <dd>
          <CommandDeckStatusPill label={statusPill.label} variant={statusPill.variant} />
          <span className="cd-mc-cockpit__synk-value">{mode === 'preview' ? 'Preview' : 'Building'}</span>
        </dd>
      </div>
      <div>
        <dt>Momentum</dt>
        <dd>{momentum}</dd>
      </div>
      <div>
        <dt>Signal</dt>
        <dd>{signal}%</dd>
      </div>
      <div>
        <dt>AI confidence</dt>
        <dd>91%</dd>
      </div>
    </dl>
  )
}

export function CommandDeckMissionControl({
  bundle,
  unlocked,
  mode,
  continueResolution,
  onPrimaryCta,
}: Props) {
  if (!unlocked) {
    return (
      <section className="cd-mc-mission command-deck-glass" aria-labelledby="cd-mc-mission-heading">
        <h2 id="cd-mc-mission-heading">Mission Control</h2>
        <CommandDeckAccessHoldPanel access={bundle.access} />
      </section>
    )
  }

  const workContext = deriveCommandDeckWorkContext({ bundle, mode, continueResolution })
  const deckPulse = deriveCommandDeckDeckPulse({ bundle, mode, continueResolution })

  return (
    <section className="cd-mc-cockpit command-deck-glass" aria-labelledby="cd-mc-cockpit-heading">
      <header className="cd-mc-cockpit__head">
        <h2 id="cd-mc-cockpit-heading">Mission Control</h2>
        <MissionPulseStrip bundle={bundle} mode={mode} />
      </header>

      <div className="cd-mc-cockpit__arena" aria-label="ORBIS spatial command environment">
        <aside className="cd-mc-cockpit__context-overlay" aria-label="Current work context overlay">
          <CommandDeckWorkContext
            model={workContext}
            mode={mode}
            profileId={bundle.accessProfileId}
          />
        </aside>

        <div className="cd-mc-cockpit__map-core" aria-label="ORBIS spatial map instrument">
          <CommandDeckSpatialMap bundle={bundle} continueResolution={continueResolution} />
        </div>

        <CommandDeckAmbientBand
          pulse={deckPulse}
          recall={workContext.recall}
          libraryAction={workContext.libraryAction}
          continueResolution={continueResolution}
          mode={mode}
          onPrimaryCta={onPrimaryCta}
        />
      </div>
    </section>
  )
}
